import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { SUPPORTED_LOCALES } from '@pingo/shared';

/**
 * Translation files: apps/web/public/locales/<lng>/<namespace>.json (editable by non-developers).
 * One namespace per feature so parallel workers never edit the same file:
 *   common, errors, auth (lead) | admin | caddy | public | signup | player | clubs | platform | account
 * Native-app-only namespaces (CLAUDE.md §16.9) live in the same folder so this parity test covers
 * them; the web never loads them: mobile (lead) | mobile-caddy | mobile-live | mobile-account |
 * mobile-clubs, mobile-personal
 * Usage in a feature: const { t } = useTranslation('caddy'); t('hole.title')
 */
export const NAMESPACES = [
  'common',
  'errors',
  'auth',
  'admin',
  'caddy',
  'public',
  'signup',
  'player',
  'clubs',
  'platform',
  'account',
  'mobile',
  'mobile-caddy',
  'mobile-live',
  'mobile-account',
  'mobile-clubs',
  'mobile-personal',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const LANGUAGE_LABELS: Record<(typeof SUPPORTED_LOCALES)[number], string> = {
  vi: 'Tiếng Việt',
  en: 'English',
  ko: '한국어',
  zh: '简体中文',
  ja: '日本語',
};

export function initI18n() {
  if (i18n.isInitialized) return i18n;
  void i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      supportedLngs: [...SUPPORTED_LOCALES],
      nonExplicitSupportedLngs: true, // zh-CN -> zh, ko-KR -> ko
      load: 'languageOnly',
      fallbackLng: 'en',
      ns: ['common', 'errors'],
      defaultNS: 'common',
      backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
      detection: {
        order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
        lookupQuerystring: 'lng',
        lookupLocalStorage: 'pingo.lng',
        caches: ['localStorage'],
      },
      interpolation: { escapeValue: false },
      react: { useSuspense: true },
    });
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
  });
  return i18n;
}

export { i18n };
